import { cn } from '@/shared/lib/utils';

export type MetricTone = 'neutral' | 'good' | 'attention';

interface MetricProps {
  label: string;
  /** Já formatado — "6 dias", "2", "—". Quem formata é quem sabe a unidade. */
  value: string;
  /**
   * A linha pequena embaixo: a meta, a comparação, o que o número quer dizer.
   * É o que separa um painel de um placar — `2` não diz nada; `2 de 9 na fila`
   * diz.
   */
  hint?: string;
  /**
   * **Só `attention` pinta.** `good` e `neutral` são iguais de propósito: um
   * painel que fica verde quando está tudo bem ensina a pessoa a procurar cor,
   * e no dia em que houver âmbar ela já terá parado de olhar. O verde existe no
   * tipo para quem for tentado — e para achar este comentário.
   */
  tone?: MetricTone;
  className?: string;
}

/**
 * Um número com o que ele significa embaixo — a unidade do bloco "Saúde da
 * esteira".
 *
 * ⚠️ **Não confundir com `StatusTiles`.** Aquele conta projetos por situação e
 * some quando o grupo é zero; este mostra uma medida derivada (mediana, prazo,
 * contagem de atraso) e **precisa** mostrar o zero — "0 parados há mais de 14
 * dias" é a boa notícia que o bloco existe para dar, e escondê-la deixaria a
 * fileira com um buraco onde estava a resposta.
 */
export function Metric({ label, value, hint, tone = 'neutral', className }: MetricProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4', className)}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          'tabular mt-1 text-2xl font-semibold',
          tone === 'attention' ? 'text-amber-600' : 'text-foreground',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
